import { Box, Paper, Popper, Typography, type PopperPlacementType } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { osirisTokens } from '@/theme/tokens';

// Only one popup per group may be open: two pending confirmations would make
// "hold Enter" ambiguous. Groups keep a settings-style popup (the controller
// mapper, which holds unsaved edits) from being closed by a confirmation.
const activePopups = new Map<string, { id: string; close: () => void }>();

interface AnchoredPopupProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  title: string;
  children: ReactNode;
  /** Footer buttons, right-aligned */
  actions?: ReactNode;
  /** Row between the header and the body, e.g. tabs */
  subheader?: ReactNode;
  /** Esc — the popup has no backdrop to click */
  onClose: () => void;
  /** Popups sharing a group close each other; default groups all confirmations */
  exclusiveGroup?: string;
  placement?: PopperPlacementType;
  /** Popper offset [skidding, distance] from the anchor, in px */
  offset?: [number, number];
  width?: number;
  className?: string;
  bodySx?: SxProps<Theme>;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  onKeyUp?: (e: KeyboardEvent<HTMLDivElement>) => void;
}

/**
 * Non-blocking card anchored to the control that opened it (Figma popup
 * 29308:123157): the map and telemetry stay visible and usable while the
 * operator decides. Portaled, so the map's overflow clipping can't cut it.
 */
export function AnchoredPopup({
  open,
  anchorEl,
  title,
  children,
  actions,
  subheader,
  onClose,
  exclusiveGroup = 'confirm',
  placement = 'top-start',
  offset = [0, 16],
  width = 430,
  className,
  bodySx,
  onKeyDown,
  onKeyUp,
}: AnchoredPopupProps) {
  // no focus trap: focus the card on mount so keyboard handling works for
  // text-only bodies. Runs after children mount, so an autoFocus'd input
  // inside keeps its focus.
  const focusCard = useCallback((el: HTMLDivElement | null) => {
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
  }, []);

  const closeRef = useRef(onClose);
  useLayoutEffect(() => {
    closeRef.current = onClose;
  });

  const id = useId();
  useEffect(() => {
    if (!open) return;
    const current = activePopups.get(exclusiveGroup);
    if (current && current.id !== id) current.close();
    activePopups.set(exclusiveGroup, { id, close: () => closeRef.current() });
    return () => {
      if (activePopups.get(exclusiveGroup)?.id === id) activePopups.delete(exclusiveGroup);
    };
  }, [open, id, exclusiveGroup]);

  // Esc must work after focus moved to the map. MUI menus/dialogs stop the
  // event's propagation for their own Esc, so whatever reaches window is ours.
  useEffect(() => {
    if (!open) return;
    const onEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeRef.current();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [open]);

  return (
    <Popper
      open={open && !!anchorEl}
      anchorEl={anchorEl}
      placement={placement}
      modifiers={[
        { name: 'offset', options: { offset } },
        { name: 'preventOverflow', options: { padding: 8 } },
      ]}
      className={`anchored-popup${className ? ' ' + className : ''}`}
      sx={{ zIndex: 'modal' }}
    >
      <Paper
        ref={focusCard}
        tabIndex={-1}
        role="dialog"
        aria-label={title}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        className="anchored-popup-card"
        sx={{
          width,
          maxWidth: 'calc(100vw - 32px)',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: osirisTokens.bg.page,
          border: `1px solid ${osirisTokens.alpha.dark130}`,
          borderRadius: '2px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        <Box
          className="anchored-popup-header"
          sx={{
            height: 32,
            px: 2,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            bgcolor: osirisTokens.bg.header,
            fontFamily: osirisTokens.fonts.display,
            fontSize: 12,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            color: osirisTokens.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Box>
        {subheader}
        <Box
          className="anchored-popup-body"
          sx={[
            { bgcolor: osirisTokens.bg.surface, minHeight: 0 },
            ...(Array.isArray(bodySx) ? bodySx : bodySx ? [bodySx] : []),
          ]}
        >
          {children}
        </Box>
        {actions && (
          <Box
            className="anchored-popup-actions"
            sx={{
              p: 2,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 2,
              bgcolor: osirisTokens.bg.surface,
            }}
          >
            {actions}
          </Box>
        )}
      </Paper>
    </Popper>
  );
}

/** Popup body copy (Figma popup): IBM Plex Mono 12 on txt-secondary */
export function PopupText({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Typography
      className={className}
      sx={{
        fontFamily: osirisTokens.fonts.mono,
        fontSize: 12,
        lineHeight: 'normal',
        color: osirisTokens.text.secondary,
        whiteSpace: 'pre-wrap',
        '& strong': { fontWeight: 600 },
      }}
    >
      {children}
    </Typography>
  );
}
